import { type ChangeEvent, type DragEvent, useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, Upload } from "lucide-react";
import { toast } from "sonner";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import {
  BULK_LINKS_FILE_EXTENSIONS,
  buildBulkLinksPrompt,
  parseBulkLinks,
  planBulkLinks,
  readBulkLinksFile,
  type BulkLinkStatus,
} from "@/lib/agent/bulkInfoLinks";
import { cn } from "@/lib/utils";
import type { InfoLink } from "@/types/agent/info-links";

interface BulkLinksPasteSheetProps {
  isOpen: boolean;
  onClose: () => void;
  existingLinks: InfoLink[];
  maxLinks: number;
  genieName?: string | null;
  onApply: (links: InfoLink[]) => void;
}

const STATUS_STYLES: Record<BulkLinkStatus, { label: string; className: string }> = {
  new: { label: "New", className: "bg-genie-primary-light text-genie-primary" },
  update: { label: "Updates existing", className: "bg-genie-secondary-light text-genie-secondary" },
  unchanged: { label: "Already added", className: "bg-gray-100 text-gray-500" },
  "over-limit": { label: "Over limit", className: "bg-genie-accent-light text-gray-700" },
};

const PLACEHOLDER = `[
  { "label": "Switchboard diagram", "url": "https://example.com/switchboard", "description": "Wiring diagram of the main switchboard — for breaker and install questions" }
]

or one per line:
Pricing - https://example.com/pricing
[Book a visit](https://example.com/book)

or spreadsheet columns:
label,url,description`;

const FILE_TYPES_LABEL = BULK_LINKS_FILE_EXTENSIONS.join(", ");

export const BulkLinksPasteSheet = ({
  isOpen,
  onClose,
  existingLinks,
  maxLinks,
  genieName,
  onApply,
}: BulkLinksPasteSheetProps) => {
  const [text, setText] = useState("");
  const [promptCopied, setPromptCopied] = useState(false);
  const [loadedFileName, setLoadedFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setText("");
      setPromptCopied(false);
      setLoadedFileName(null);
      setIsDragging(false);
    }
  }, [isOpen]);

  const loadFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      setText(await readBulkLinksFile(file));
      setLoadedFileName(file.name);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't read this file. Please try again.");
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    void loadFile(file);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    setIsDragging(false);
    void loadFile(event.dataTransfer.files[0]);
  };

  const parsed = useMemo(() => parseBulkLinks(text), [text]);
  const plan = useMemo(() => planBulkLinks(existingLinks, parsed.rows, maxLinks), [existingLinks, parsed.rows, maxLinks]);
  const applicableCount = plan.counts.new + plan.counts.update;
  const hasInput = text.trim().length > 0;

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(buildBulkLinksPrompt(genieName));
      setPromptCopied(true);
      toast.success("Prompt copied — paste it into your AI assistant");
    } catch {
      toast.error("Couldn't copy the prompt. Please try again.");
    }
  };

  const handleApply = () => {
    if (applicableCount === 0) return;
    onApply(plan.links);
    const parts = [
      plan.counts.new ? `${plan.counts.new} added` : null,
      plan.counts.update ? `${plan.counts.update} updated` : null,
    ].filter(Boolean);
    toast.success(`${parts.join(", ")} — remember to save`);
    onClose();
  };

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Add links in bulk"
      description="Upload a file of links or paste a list — a spreadsheet export, JSON, a markdown list, or one link per line."
      size="lg"
      footer={
        <div className="flex w-full justify-start gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={applicableCount === 0}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
          >
            {applicableCount > 0 ? `Apply ${applicableCount} link${applicableCount === 1 ? "" : "s"}` : "Apply links"}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
          <div className="rounded-[10px] bg-white p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold text-gray-900">Let your AI do the typing</p>
                <p className="text-sm text-gray-600 mt-1">
                  Copy this prompt into ChatGPT, Claude or similar, then paste its answer below. Descriptions help
                  your Genie show the right resource at the right moment.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleCopyPrompt()}
                className="min-h-[44px] flex-shrink-0 gap-1.5"
              >
                {promptCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {promptCopied ? "Copied" : "Copy AI prompt"}
              </Button>
            </div>
          </div>
        </div>

        <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <label htmlFor="bulk-links-input" className="text-sm font-semibold text-gray-700">
              Links
            </label>
            <div className="flex items-center gap-3">
              <span className="min-w-0 truncate text-xs text-gray-500">
                {loadedFileName ? `Loaded from ${loadedFileName}` : `Drop a file here, or pick one (${FILE_TYPES_LABEL})`}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept={BULK_LINKS_FILE_EXTENSIONS.join(",")}
                className="hidden"
                onChange={handleFileChange}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="min-h-[44px] flex-shrink-0 gap-1.5"
              >
                <Upload className="w-4 h-4" />
                Upload file
              </Button>
            </div>
          </div>
          <textarea
            id="bulk-links-input"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setLoadedFileName(null);
            }}
            placeholder={PLACEHOLDER}
            rows={10}
            spellCheck={false}
            className={cn(
              "w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base sm:text-sm font-mono transition-colors",
              isDragging ? "border-dashed border-genie-primary bg-genie-primary/5" : "border-gray-300",
            )}
          />
          <p className="text-xs text-gray-500 mt-2">
            Links whose URL is already listed get their label and description updated. Up to {maxLinks} links in total.
            Missing descriptions are written for you after you apply, and every new link is checked that it opens.
          </p>
        </div>

        {hasInput && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-700">
              Preview
              <span className="ml-2 font-normal text-gray-500">
                {plan.counts.new} new · {plan.counts.update} updated
                {plan.counts.unchanged ? ` · ${plan.counts.unchanged} unchanged` : ""}
                {plan.counts["over-limit"] ? ` · ${plan.counts["over-limit"]} over limit` : ""}
                {parsed.rejected.length ? ` · ${parsed.rejected.length} skipped` : ""}
              </span>
            </p>

            {plan.entries.length > 0 && (
              <ul className="space-y-2">
                {plan.entries.map(({ row, status }) => (
                  <li
                    key={row.url}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50",
                      (status === "unchanged" || status === "over-limit") && "opacity-60",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{row.label}</div>
                      <div className="text-xs text-gray-500 truncate">{row.url}</div>
                      {row.description && <div className="text-xs text-gray-600 mt-1 line-clamp-2">{row.description}</div>}
                    </div>
                    <span
                      className={cn(
                        "flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                        STATUS_STYLES[status].className,
                      )}
                    >
                      {STATUS_STYLES[status].label}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {parsed.rejected.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs font-semibold text-gray-700 mb-1">Skipped</p>
                <ul className="space-y-1">
                  {parsed.rejected.map((item, index) => (
                    <li key={`${index}-${item.raw}`} className="text-xs text-gray-500 truncate">
                      <span className="text-red-600">{item.reason}:</span> {item.raw}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </BaseSheet>
  );
};
